'use client';

import { useTranslations } from 'next-intl';

import ArticleHiddenContentWrapper from '../ArticleHiddenContentWrapper';
import ArticlePremiumBanner from '../atoms/ArticlePremiumBanner';
import { ArticlePremiumContentPercentageSimple } from '../atoms/ArticlePremiumContentPercentage';
import NewsBlockedBanner, { BannerView } from '../atoms/NewsBlockedBanner';

import { useFeatureIsActive } from '@components/Only/FeatureIsActive';
import { useDelayedProp } from '@hooks/useDelayedState';
import { useUserStore } from '@utils/user';

// import ArticlePremiumBanner from '../atoms/ArticlePremiumBanner';

const PremiumContentCover = ({ viewType, percentageLeft }) => {
  const t = useTranslations('premium');

  if (viewType === 'podcast') {
    return (
      <div className="absolute z-10 flex h-full w-full flex-col items-center justify-center text-lg font-bold text-premium-900 lg:text-2xl">
        <p className="px-4 text-center dark:text-white">{t('contentCover.textPodcast')}</p>
      </div>
    );
  }

  return <ArticlePremiumContentPercentageSimple percentage={percentageLeft} />;
};

export const useBannerInitialState = (premiumRequired, loggedInRequired, newsletterRequired, isBlockadeNecessary) => {
  const { isPremium, isLoggedIn, hasNewsletter, isReady } = useUserStore();
  const delayedLoggedIn = useDelayedProp(5000, !isReady || isLoggedIn || isPremium, (newValue) => !newValue);
  const isNewsAbleToBeBlocked = useFeatureIsActive('article.newsAbleToBeBlocked');

  let shouldBeBlocked = false;
  if (!isPremium && premiumRequired) {
    shouldBeBlocked = true;
  }
  if ((!isLoggedIn || !delayedLoggedIn) && loggedInRequired) {
    shouldBeBlocked = true;
  }

  if (!hasNewsletter && newsletterRequired && !isPremium) {
    shouldBeBlocked = true;
  }
  if (!isReady) return [undefined];

  if (!isNewsAbleToBeBlocked) {
    shouldBeBlocked = !isPremium && premiumRequired;
  }
  if (!isBlockadeNecessary || !shouldBeBlocked) return [undefined];

  let bannerInitialState: BannerView | undefined = 'LoggedOutForm';
  if (isLoggedIn && (hasNewsletter || isPremium)) {
    bannerInitialState = undefined;
  } else if (isLoggedIn && !isPremium && !hasNewsletter) {
    bannerInitialState = 'NewsletterForm';
  }

  return [bannerInitialState, isPremium] as const;
};

export default function ArticlePaidBlockade({
  article,
  children,
  alternativeContent,
  percentageLeft,
  viewType,
  isBlockadeNecessary,
  premiumRequired,
  loggedInRequired,
  newsletterRequired,
  analyticsData,
}) {
  const [bannerInitialState, isPremium] = useBannerInitialState(
    premiumRequired,
    loggedInRequired,
    newsletterRequired,
    isBlockadeNecessary,
  );
  const isPremiumEnabled = useFeatureIsActive('premium.enabled');

  if (bannerInitialState === undefined) return children;

  return (
    <>
      {percentageLeft > 0 && (
        <ArticleHiddenContentWrapper
          className="article-content mb-3"
          contentCover={<PremiumContentCover viewType={viewType} percentageLeft={percentageLeft} />}
        >
          {alternativeContent}
        </ArticleHiddenContentWrapper>
      )}
      {!isPremium && premiumRequired && isPremiumEnabled && (
        <ArticlePremiumBanner className="mb-4" viewType={viewType} article={article} />
      )}
      {(!premiumRequired || !isPremiumEnabled) && (
        <NewsBlockedBanner initialState={bannerInitialState} analyticsData={analyticsData} />
      )}
    </>
  );
}
